// Запросы встроенного ИИ: /api/ai/* (статус и настройки, совет, отчёты, черновик плана, чат, токены).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

/** Модель решений Jev: выбор из вариантов с вероятностями, без генерации текста. */
export type JevStatus = {
  configured: boolean
  key_set: boolean
  base_url: string
  model: string
  base_url_source: 'personal' | 'global' | 'env' | 'default' | 'settings'
  model_source: 'personal' | 'global' | 'env' | 'default' | 'settings'
  min_confidence: number
  message: string | null
}

export type AiStatus = {
  configured: boolean
  key_set: boolean
  base_url: string
  model: string
  base_url_source: 'personal' | 'global' | 'env' | 'default' | 'settings'
  model_source: 'personal' | 'global' | 'env' | 'default' | 'none' | 'settings'
  message: string | null
  jev: JevStatus
  budget?: AiBudget
  budget_month?: string
  external_allowed?: boolean
  available?: boolean
  unavailable_reason?: string | null
}
export type AiBudget = { limit_rub: string | null; confirmed_rub: string | null; reserved_rub: string | null; remaining_rub: string | null; pending_count: number | null }

export type AiSettings = {
  base_url_override: string | null
  model_override: string | null
  env_base_url: string
  env_model: string
  key_set: boolean
  jev_base_url_override: string | null
  jev_model_override: string | null
  env_jev_base_url: string
  env_jev_model: string
  jev_key_set: boolean
  jev_min_confidence: number
  advice_time: string
  weekly_report_dow: number
  weekly_report_time: string
}

export type AiSettingsPatch = Partial<
  Pick<
    AiSettings,
    | 'base_url_override'
    | 'model_override'
    | 'jev_base_url_override'
    | 'jev_model_override'
    | 'jev_min_confidence'
    | 'advice_time'
    | 'weekly_report_dow'
    | 'weekly_report_time'
  >
>

export type CheckResult = { ok: boolean; model: string | null; reply: string | null; error: string | null }

export type UsageKind = {
  kind: 'advice' | 'weekly' | 'plan' | 'chat'
  label: string
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
}
export type UsageEntry = { month: string; kind: string; role: 'llm' | 'jev'; calls: number; prompt_tokens: number | null; completion_tokens: number | null; cost_rub: string | null; pending_count: number }
export type Usage = { month: string; kinds: UsageKind[]; total_tokens: number; usage_available?: boolean; usage?: UsageEntry[]; budget?: AiBudget }

export type Report = {
  id: string
  kind: 'daily_advice' | 'weekly'
  period_start: string
  period_end: string
  content_md: string | null
  model: string | null
  status: 'ok' | 'error'
  error: string | null
  prompt_tokens: number | null
  completion_tokens: number | null
  created_at: string
  updated_at: string
}
export type ReportPage = { items: Report[]; next_cursor: string | null }
export type AdviceToday = { configured: boolean; day: string; report: Report | null }

export type Equipment = 'barbell' | 'dumbbell' | 'smith' | 'machine' | 'cable' | 'bodyweight' | 'kettlebell' | 'band' | 'other'

export type PlanRequest = {
  goal: string
  sessions_per_week: number
  minutes_per_session: number
  equipment: Equipment[]
  constraints: string | null
}

export type CardioSpec = {
  sport: 'run' | 'bike' | 'walk' | 'swim' | 'other'
  duration_s?: number | null
  distance_m?: number | null
  hr_zone?: number | null
}

export type ProposalExercise = {
  name: string
  exercise_id: string | null
  exercise_name: string | null
  tracking: string | null
  /** Подобрал Jev с такой уверенностью (0…1); null — совпало по имени или выбрано вручную. */
  match_confidence?: number | null
  sets: number
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
  notes: string | null
}

export type WorkoutFocus = 'chest' | 'back' | 'shoulders' | 'arms' | 'legs' | 'glutes' | 'core' | 'full_body' | 'cardio'
export type WorkoutIntensity = 'light' | 'moderate' | 'hard'

export type WorkoutRequest = {
  focus: WorkoutFocus[]
  minutes: number
  intensity: WorkoutIntensity
  equipment: Equipment[]
  notes: string | null
}

export type WorkoutSuggestion = { id: string; name: string; tracking: string }

export type WorkoutExercise = ProposalExercise & { suggestions: WorkoutSuggestion[] }

/** Разовая тренировка от ИИ: ничего не сохранено, начинается на клиенте. */
export type WorkoutDraft = { title: string; summary: string; exercises: WorkoutExercise[]; model: string | null }

/** Правка шаблона или тренировки словами (`POST /ai/edit`). */
export type EditMode = 'template' | 'workout'
export type EditSet = {
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
}
export type EditItem = {
  key: string
  exercise_id: string | null
  name: string
  tracking: string
  sets: EditSet[]
  /** На тренировке: есть выполненные подходы — ИИ упражнение не меняет. */
  locked?: boolean
}
export type EditTurn = { role: 'user' | 'assistant'; content: string }
export type EditRequest = {
  mode: EditMode
  title: string | null
  instruction: string
  exercises: EditItem[]
  history: EditTurn[]
}
export type EditOperation = {
  op: 'add' | 'remove' | 'replace' | 'update' | 'move'
  /** Ключ упражнения клиента (remove, replace, update, move). */
  key: string | null
  /** add/move: после какого упражнения, 'start' — в начало, null — в конец. */
  after: string | null
  /** add/replace: какое упражнение. */
  exercise: WorkoutExercise | null
  /** update: новое значение; null — оставить как есть. */
  sets: number | null
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
}
export type EditReply = { reply: string; operations: EditOperation[]; skipped: string[]; model: string | null }

/** Разбор завершённой тренировки; `none` — разбора нет и сам он не появится. */
export type SessionReview = {
  status: 'none' | 'pending' | 'ok' | 'error' | 'not_configured'
  content_md: string | null
  error: string | null
  generated_at: string | null
}

export type SessionCheckSet = {
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  completed: boolean
  is_warmup: boolean
}

/** Идущая тренировка с устройства — для совета «хватит или добавить». */
export type SessionCheckRequest = {
  title: string | null
  started_at: string
  exercises: {
    exercise_id: string
    name: string
    tracking: string
    reason: string | null
    sets: SessionCheckSet[]
  }[]
}

export type SessionCheckReply = { reply: string; model: string | null }

export type ProposalTemplate = { key: string; name: string; notes: string | null; exercises: ProposalExercise[] }

export type ProposalRule = {
  weekday: number
  kind: 'strength' | 'cardio'
  template: string | null
  template_name: string | null
  cardio: CardioSpec | null
}

export type ProposalBody = {
  summary: string
  templates: ProposalTemplate[]
  schedule: ProposalRule[]
  applied?: { template_ids: string[]; rule_ids: string[] }
}

export type ProposalIssue = {
  kind: 'schema' | 'unknown_exercise'
  message: string
  template_index: number | null
  exercise_index: number | null
  name: string | null
  suggestions: { id: string; name: string }[]
}

export type Proposal = {
  id: string
  status: 'pending' | 'applied' | 'rejected' | 'invalid'
  input: PlanRequest
  proposal: ProposalBody | null
  issues: ProposalIssue[]
  model: string | null
  created_at: string
  updated_at: string
}

export type ExerciseFix = {
  template_index: number
  exercise_index: number
  action: 'replace' | 'remove'
  exercise_id?: string
}

export type ToolCallLog = { name: string; arguments: unknown; status: 'ok' | 'error' | 'refused' | 'limit' }
export type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string | null
  tool_calls: ToolCallLog[]
  created_at: string
}
export type Conversation = { id: string; title: string | null; created_at: string; updated_at: string }
export type ConversationDetail = Conversation & { messages: ChatMessage[] }
export type ChatReply = {
  conversation_id: string
  user_message: ChatMessage
  assistant_message: ChatMessage | null
}

export const aiKeys = {
  all: ['ai'] as const,
  status: ['ai', 'status'] as const,
  settings: ['ai', 'settings'] as const,
  usage: ['ai', 'usage'] as const,
  adviceToday: ['ai', 'advice', 'today'] as const,
  reports: (kind: Report['kind'] | null) => ['ai', 'reports', kind ?? 'all'] as const,
  proposals: ['ai', 'plan-proposals'] as const,
  conversations: ['ai', 'chat', 'conversations'] as const,
  conversation: (id: string) => ['ai', 'chat', 'conversations', id] as const,
  sessionReview: (id: string) => ['ai', 'session-review', id] as const,
}

export const aiApi = {
  status: () => api.get<AiStatus>('/ai/status'),
  settings: () => api.get<AiSettings>('/ai/settings'),
  saveSettings: (patch: AiSettingsPatch) => api.put<AiSettings>('/ai/settings', patch),
  check: (body: { base_url?: string | null; model?: string | null }) => api.post<CheckResult>('/ai/check', body),
  checkJev: (body: { base_url?: string | null; model?: string | null }) => api.post<CheckResult>('/ai/jev/check', body),
  usage: () => api.get<Usage>('/ai/usage'),
  adviceToday: () => api.get<AdviceToday>('/ai/advice/today'),
  refreshAdvice: () => api.post<Report>('/ai/advice/today/refresh'),
  reports: (kind: Report['kind'] | null) => api.get<ReportPage>('/ai/reports', { query: { kind, limit: 100 } }),
  generateWeekly: (weekStart?: string) => api.post<Report>('/ai/reports/weekly', weekStart ? { week_start: weekStart } : {}),
  proposals: () => api.get<Proposal[]>('/ai/plan-proposals'),
  propose: (body: PlanRequest) => api.post<Proposal>('/ai/plan-proposals', body),
  workout: (body: WorkoutRequest) => api.post<WorkoutDraft>('/ai/workout', body),
  edit: (body: EditRequest) => api.post<EditReply>('/ai/edit', body),
  sessionReview: (id: string) => api.get<SessionReview>(`/sessions/${id}/review`),
  requestSessionReview: (id: string) => api.post<SessionReview>(`/sessions/${id}/review`),
  sessionCheck: (body: SessionCheckRequest) => api.post<SessionCheckReply>('/ai/session-check', body),
  resolve: (id: string, fix: ExerciseFix) => api.post<Proposal>(`/ai/plan-proposals/${id}/resolve`, fix),
  apply: (id: string) => api.post<Proposal>(`/ai/plan-proposals/${id}/apply`),
  reject: (id: string) => api.post<Proposal>(`/ai/plan-proposals/${id}/reject`),
  conversations: () => api.get<Conversation[]>('/ai/chat/conversations'),
  conversation: (id: string) => api.get<ConversationDetail>(`/ai/chat/conversations/${id}`),
  createConversation: () => api.post<Conversation>('/ai/chat/conversations', {}),
  deleteConversation: (id: string) => api.delete(`/ai/chat/conversations/${id}`),
  send: (id: string, text: string) => api.post<ChatReply>(`/ai/chat/conversations/${id}/messages`, { text }),
}

export function useAiStatus() {
  return useQuery({ queryKey: aiKeys.status, queryFn: aiApi.status, staleTime: 60_000 })
}

export function useProposeWorkout() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: aiApi.workout,
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.usage }),
  })
}

export function useEditWithAi() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: aiApi.edit,
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.usage }),
  })
}

export function useAdviceToday() {
  return useQuery({ queryKey: aiKeys.adviceToday, queryFn: aiApi.adviceToday })
}

export function useRefreshAdvice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: aiApi.refreshAdvice,
    onSuccess: (report) => {
      client.setQueryData<AdviceToday>(aiKeys.adviceToday, (old) =>
        old ? { ...old, report } : { configured: true, day: report.period_start, report },
      )
    },
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.usage }),
  })
}

export function useReports(kind: Report['kind'] | null) {
  return useQuery({ queryKey: aiKeys.reports(kind), queryFn: () => aiApi.reports(kind) })
}

export function useGenerateWeekly() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (weekStart?: string) => aiApi.generateWeekly(weekStart),
    onSettled: () => client.invalidateQueries({ queryKey: ['ai', 'reports'] }),
  })
}

export function useProposals() {
  return useQuery({ queryKey: aiKeys.proposals, queryFn: aiApi.proposals })
}

function useProposalMutation<A>(fn: (args: A) => Promise<Proposal>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (proposal) => {
      client.setQueryData<Proposal[]>(aiKeys.proposals, (old) => {
        const rest = (old ?? []).filter((p) => p.id !== proposal.id)
        return [proposal, ...rest].sort((a, b) => b.created_at.localeCompare(a.created_at))
      })
    },
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.proposals }),
  })
}

export function usePropose() {
  return useProposalMutation(aiApi.propose)
}

export function useResolveProposal() {
  return useProposalMutation(({ id, fix }: { id: string; fix: ExerciseFix }) => aiApi.resolve(id, fix))
}

export function useApplyProposal() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: aiApi.apply,
    // Появились шаблоны и расписание — обновить и экраны плана, и список черновиков.
    onSettled: () => client.invalidateQueries(),
  })
}

export function useRejectProposal() {
  return useProposalMutation(aiApi.reject)
}

export function useConversations() {
  return useQuery({ queryKey: aiKeys.conversations, queryFn: aiApi.conversations })
}

export function useConversation(id: string | null) {
  return useQuery({
    queryKey: aiKeys.conversation(id ?? 'none'),
    queryFn: () => aiApi.conversation(id!),
    enabled: id !== null,
  })
}

export function useAiSettings() {
  return useQuery({ queryKey: aiKeys.settings, queryFn: aiApi.settings })
}

export function useSaveAiSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: aiApi.saveSettings,
    onSuccess: (data) => client.setQueryData(aiKeys.settings, data),
    onSettled: () => client.invalidateQueries({ queryKey: aiKeys.status }),
  })
}

export function useCheckConnection() {
  return useMutation({ mutationFn: aiApi.check })
}

export function useCheckJev() {
  return useMutation({ mutationFn: aiApi.checkJev })
}

export function useUsage() {
  return useQuery({ queryKey: aiKeys.usage, queryFn: aiApi.usage })
}
